import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  stepTriggerSchema,
  stepActionSchema,
  stepRulesSchema,
  buildBenefitRulePayload,
  type StepTriggerData,
  type StepActionData,
  type StepRulesData,
} from '../types'
import { StepIndicator } from './StepIndicator'
import { StepTriggerForm } from './StepTriggerForm'
import { StepActionForm } from './StepActionForm'
import { StepRulesForm } from './StepRulesForm'

// ─── Props ──────────────────────────────────────────────────────────────────

interface BenefitRuleFormProps {
  defaultTriggerValues?: Partial<StepTriggerData>
  defaultActionValues?: Partial<StepActionData>
  defaultRulesValues?: Partial<StepRulesData>
  onSubmit: (payload: Record<string, unknown>) => void
  isSubmitting: boolean
  submitLabel?: string
  error?: string | null
}

// ─── Main Form Component ───────────────────────────────────────────────────

export function BenefitRuleForm({
  defaultTriggerValues,
  defaultActionValues,
  defaultRulesValues,
  onSubmit,
  isSubmitting,
  submitLabel = 'Crear regla',
  error,
}: BenefitRuleFormProps) {
  const [step, setStep] = useState(1)
  const [triggerData, setTriggerData] = useState<StepTriggerData | null>(null)
  const [actionData, setActionData] = useState<StepActionData | null>(null)

  // ─── Step 1 form ────────────────────────────────────────────────────────

  const triggerForm = useForm<StepTriggerData>({
    resolver: zodResolver(stepTriggerSchema),
    defaultValues: {
      name: '',
      description: '',
      internal_notes: '',
      trigger_days_before: 7,
      trigger_category: 'bronce',
      trigger_recurrence: 'first_monday',
      trigger_timezone: 'America/Santiago',
      trigger_channel: 'all',
      ...defaultTriggerValues,
    },
  })

  // ─── Step 2 form ────────────────────────────────────────────────────────

  const actionForm = useForm<StepActionData>({
    resolver: zodResolver(stepActionSchema),
    defaultValues: {
      coupon_discount_type: 'percent',
      coupon_amount: 10,
      coupon_max_discount: 0,
      coupon_code_prefix: 'CLUB',
      gift_description: '',
      gift_valid_days: 30,
      shipping_condition: 'first_purchase',
      shipping_min_amount: 0,
      bonus_points: 100,
      email_template: '',
      email_subject: '',
      ...defaultActionValues,
    },
  })

  // ─── Step 3 form ────────────────────────────────────────────────────────

  const rulesForm = useForm<StepRulesData>({
    resolver: zodResolver(stepRulesSchema),
    defaultValues: {
      eligible_categories: ['bronce'],
      single_use: false,
      valid_days: 30,
      valid_days_unlimited: false,
      max_grants_per_year: 1,
      max_grants_per_year_unlimited: false,
      max_grants_per_month: undefined,
      max_grants_per_month_unlimited: true,
      min_purchase: undefined,
      min_purchase_none: true,
      max_discount: undefined,
      max_discount_none: true,
      valid_channels: ['web'],
      non_combinable: false,
      requires_email: false,
      cooldown_days: undefined,
      cooldown_days_none: true,
      valid_until_first_purchase: false,
      priority: 10,
      ...defaultRulesValues,
    },
  })

  // ─── Navigation ─────────────────────────────────────────────────────────

  const handleNextStep1 = triggerForm.handleSubmit((data) => {
    setTriggerData(data)
    setStep(2)
  })

  const handleNextStep2 = actionForm.handleSubmit((data) => {
    setActionData(data)

    // When trigger is category_reached, lock eligible_categories to the
    // trigger category — the eligibility check would be redundant otherwise.
    if (triggerData?.trigger_type === 'category_reached' && triggerData.trigger_category) {
      rulesForm.setValue('eligible_categories', [triggerData.trigger_category], { shouldValidate: true })
    }

    setStep(3)
  })

  const handleFinalSubmit = rulesForm.handleSubmit(
    (rulesData) => {
      if (!triggerData || !actionData) return
      const payload = buildBenefitRulePayload(triggerData, actionData, rulesData)
      onSubmit(payload)
    },
    (errors) => {
      console.error('[BenefitRuleForm] Step 3 validation errors:', errors)
    },
  )

  const handleBack = () => {
    if (step === 2) setStep(1)
    else if (step === 3) setStep(2)
  }

  return (
    <div className="max-w-3xl">
      <StepIndicator currentStep={step} />

      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {step === 1 && (
        <StepTriggerForm form={triggerForm} onSubmit={handleNextStep1} />
      )}

      {step === 2 && (
        <StepActionForm form={actionForm} onSubmit={handleNextStep2} onBack={handleBack} />
      )}

      {step === 3 && (
        <StepRulesForm
          form={rulesForm}
          triggerData={triggerData}
          actionData={actionData}
          onSubmit={handleFinalSubmit}
          onBack={handleBack}
          isSubmitting={isSubmitting}
          submitLabel={submitLabel}
        />
      )}
    </div>
  )
}
